import React, { useEffect, useState } from 'react';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import Footer from 'examples/Footer';
import DataTable from 'examples/Tables/DataTable';
import MDBadge from 'components/MDBadge';
import TopNavbar from 'examples/Navbars/TopNavbar';
import { useData } from '../../hooks';
import { handleDownloadExcel } from '../../components/excel/excelUtils';
import MDButton from '../../components/MDButton';
import { apiGetCafe24PointConversionList } from '../../api/point.api';

const getTodayYmd = () => new Date().toISOString().split('T')[0];

const formatDateYmd = (dateString) => {
  const options = { year: 'numeric', month: '2-digit', day: '2-digit' };
  return new Date(dateString)
    .toLocaleString('ko-KR', options)
    .replace(/. /g, '-')
    .replace('.', '')
    .replace(' ', ' ')
    .slice(0, 10);
};

const Cafe24PointConversionList = () => {
  const [conversions, setConversions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  const [zerosalonUserId, setZerosalonUserId] = useState('');
  const [cafe24MemberId, setCafe24MemberId] = useState('');
  const [status, setStatus] = useState('');
  const [createdFrom, setCreatedFrom] = useState('');
  const [createdTo, setCreatedTo] = useState('');

  const { user, appIsReady } = useData();

  useEffect(() => {
    const today = getTodayYmd();
    setCreatedFrom(today);
    setCreatedTo(today);
  }, []);

  useEffect(() => {
    if (createdFrom && createdTo && appIsReady && user) {
      fetchConversions(page);
    }
  }, [page, user, appIsReady, createdFrom, createdTo]);

  const fetchConversions = async (pageNum) => {
    setLoading(true);
    try {
      const params = {
        page: pageNum,
        limit: 10,
        zerosalonUserId,
        cafe24MemberId,
        status: status || undefined,
        createdFrom,
        createdTo,
      };

      const response = await apiGetCafe24PointConversionList(params, user.accessToken);
      if (response && response.data) {
        setConversions(response.data);
        setTotal(response.total);
      }
    } catch (error) {
      console.error('Failed to load cafe24 point conversions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPage(1);
    fetchConversions(1);
  };

  const columns = [
    { Header: 'ID', accessor: 'id', align: 'left' },
    { Header: 'User ID', accessor: 'zerosalonUserId', align: 'left' },
    { Header: '이름', accessor: 'userName', align: 'left' },
    { Header: 'email', accessor: 'userEmail', align: 'left' },
    { Header: 'Cafe24 ID', accessor: 'cafe24MemberId', align: 'left' },
    { Header: '전환 R포인트', accessor: 'amount', align: 'left' },
    { Header: '상태', accessor: 'status', align: 'left' },
    { Header: '사유', accessor: 'reason', align: 'left' },
    { Header: '전환일', accessor: 'createdAt', align: 'left' },
  ];

  const rows = conversions.map((row) => ({
    id: row.id,
    zerosalonUserId: row.zerosalonUserId,
    userName: row.userName ?? '-',
    userEmail: row.userEmail ?? '-',
    cafe24MemberId: row.cafe24MemberId,
    amount: row.amount,
    status: (
      <MDBadge
        badgeContent={row.status}
        color={row.status === 'SUCCESS' ? 'success' : 'error'}
        variant="gradient"
        size="sm"
      />
    ),
    reason: row.reason ?? '-',
    createdAt: formatDateYmd(row.createdAt),
  }));

  return (
    <DashboardLayout>
      <TopNavbar />

      <MDBox display="flex" mr={2} justifyContent="right">
        <MDButton
          variant="gradient"
          color="info"
          onClick={() => handleDownloadExcel('제로살롱몰 포인트 전환 내역', columns, rows)}
          sx={{ ml: 2 }}
        >
          엑셀 다운로드
        </MDButton>
      </MDBox>

      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  제로살롱몰 포인트 전환내역
                </MDTypography>
              </MDBox>

              <MDBox p={3}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6} md={2}>
                        <TextField
                          label="User ID"
                          fullWidth
                          value={zerosalonUserId}
                          onChange={(e) => setZerosalonUserId(e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={2}>
                        <TextField
                          label="Cafe24 ID"
                          fullWidth
                          value={cafe24MemberId}
                          onChange={(e) => setCafe24MemberId(e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={2}>
                        <TextField
                          label="상태"
                          select
                          fullWidth
                          value={status}
                          onChange={(e) => setStatus(e.target.value)}
                        >
                          <MenuItem value="">전체</MenuItem>
                          <MenuItem value="SUCCESS">SUCCESS</MenuItem>
                          <MenuItem value="FAIL">FAIL</MenuItem>
                        </TextField>
                      </Grid>
                      <Grid item xs={12} sm={6} md={2}>
                        <TextField
                          label="Created From"
                          type="date"
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                          value={createdFrom}
                          onChange={(e) => setCreatedFrom(e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={2}>
                        <TextField
                          label="Created To"
                          type="date"
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                          value={createdTo}
                          onChange={(e) => setCreatedTo(e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={2}>
                        <Button variant="contained" color="success" fullWidth onClick={handleSearch}>
                          검색
                        </Button>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </MDBox>

              <MDBox pt={3}>
                <DataTable
                  table={{ columns, rows }}
                  isSorted={false}
                  entriesPerPage={true}
                  showTotalEntries={true}
                  noEndBorder
                  loading={loading}
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <Footer />
    </DashboardLayout>
  );
};

export default Cafe24PointConversionList;

